import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  Cloud,
  ChevronUp,
  Clock,
  Play,
  Plus,
  Save,
  Trash2,
  Webhook,
  Zap,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Card, CardBody } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Field, Input, Select, Textarea } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { SystemLogo } from "../components/connector/SystemLogo";
import { useAuth } from "../lib/auth";
import {
  ApiError,
  builderConnections,
  createEndpoint,
  createWorkflow,
  getWorkflow,
  runWorkflow,
  updateWorkflow,
} from "../lib/api";
import type {
  BuilderConnection,
  WorkflowRun,
  WorkflowStep,
  WorkflowTrigger,
} from "../lib/api";
import { cn } from "../lib/cn";

/** Default param keys per generic operation — the five-verb vocabulary. */
const OP_PARAMS: Record<string, string[]> = {
  query: ["filter", "limit"],
  create: ["fields"],
  update: ["id", "fields"],
  delete: ["id"],
  subscribe: ["event"],
};

function opOf(capability: string | null): string {
  return (capability ?? "").split(".")[1] ?? "";
}

let keyCounter = 0;
function freshKey(): string {
  keyCounter += 1;
  return `step${keyCounter}`;
}

function blankStep(): WorkflowStep {
  return { key: freshKey(), type: "action", name: "", connection: null, capability: null, params: {} };
}

export default function WorkflowBuilder() {
  const { slug } = useParams<{ slug: string }>();
  const editing = slug && slug !== "new";
  const { currentTenant } = useAuth();
  const tenant = currentTenant?.slug ?? "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!!editing);
  const [conns, setConns] = useState<BuilderConnection[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [trigger, setTrigger] = useState<WorkflowTrigger>({ type: "manual" });
  const [steps, setSteps] = useState<WorkflowStep[]>([blankStep()]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [run, setRun] = useState<WorkflowRun | null>(null);
  const [running, setRunning] = useState(false);
  const [exposing, setExposing] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    builderConnections(tenant).then(setConns).catch(() => setConns([]));
  }, [tenant]);

  useEffect(() => {
    if (!editing || !tenant) return;
    setLoading(true);
    getWorkflow(tenant, slug!)
      .then((w) => {
        setName(w.name);
        setDescription(w.description);
        setTrigger(w.trigger);
        setSteps(
          w.steps.length
            ? w.steps.map((s) => ({ ...s, params: s.params ?? {} }))
            : [blankStep()],
        );
      })
      .catch(() => setBanner("Couldn't load this workflow."))
      .finally(() => setLoading(false));
  }, [editing, slug, tenant]);

  const connBySlug = useMemo(
    () => Object.fromEntries(conns.map((c) => [c.slug, c])),
    [conns],
  );

  function patchStep(index: number, patch: Partial<WorkflowStep>) {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function pickConnection(index: number, connSlug: string) {
    patchStep(index, { connection: connSlug || null, capability: null, params: {} });
  }

  function pickCapability(index: number, capability: string) {
    // Prefill the param keys this operation expects.
    const params: Record<string, unknown> = {};
    for (const key of OP_PARAMS[opOf(capability)] ?? []) params[key] = "";
    patchStep(index, { capability: capability || null, params });
  }

  function move(index: number, dir: -1 | 1) {
    setSteps((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }

  function body() {
    return {
      name,
      description,
      trigger,
      steps: steps.map((s) => ({ ...s, name: s.name || "" })),
      enabled: true,
    };
  }

  async function save(): Promise<string | null> {
    setSaving(true);
    setErrors({});
    setBanner(null);
    try {
      const saved = editing
        ? await updateWorkflow(tenant, slug!, body())
        : await createWorkflow(tenant, body());
      if (!editing) navigate(`/app/workflows/${saved.slug}`, { replace: true });
      return saved.slug;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields) setErrors(err.fields);
        setBanner(err.message);
      } else {
        setBanner("Couldn't reach the server.");
      }
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function exposeAsApi() {
    // Save first so the endpoint binds the latest definition, then hand off to
    // the API Designer with this workflow pre-selected.
    const savedSlug = await save();
    if (!savedSlug) return;
    setExposing(true);
    try {
      await createEndpoint(tenant, {
        name: `${name || savedSlug} API`,
        method: "POST",
        path: savedSlug,
        workflow_slug: savedSlug,
        auth_type: "client_credentials",
        auth: {},
      });
      navigate("/app/apis");
    } catch (err) {
      // A 409 means an endpoint already serves that path — still go there.
      if (err instanceof ApiError && err.status === 409) navigate("/app/apis");
      else setBanner(err instanceof ApiError ? err.message : "Couldn't expose this workflow.");
    } finally {
      setExposing(false);
    }
  }

  async function saveAndRun() {
    const savedSlug = await save();
    if (!savedSlug) return;
    setRunning(true);
    setRun(null);
    try {
      setRun(await runWorkflow(tenant, savedSlug));
    } catch (err) {
      setBanner(err instanceof ApiError ? err.message : "Couldn't run the workflow.");
    } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="h-8 w-8 text-brand-500" />
      </div>
    );
  }

  const priorKeys = (index: number) => steps.slice(0, index).map((s) => s.key);

  return (
    <>
      <div className="mb-2">
        <Link to="/app/workflows" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Workflows
        </Link>
      </div>
      <PageHeader
        title={editing ? "Edit workflow" : "New workflow"}
        description="Chain connector operations into an automated job. Each step runs in the sandbox; later steps can use earlier results."
        actions={
          <div className="flex items-center gap-2">
            {editing && (
              <Button variant="outline" onClick={exposeAsApi} loading={exposing}>
                <Cloud className="h-4 w-4" /> Expose as API
              </Button>
            )}
            <Button variant="outline" onClick={saveAndRun} loading={running} disabled={saving}>
              <Play className="h-4 w-4" /> Save & run
            </Button>
            <Button onClick={save} loading={saving}>
              <Save className="h-4 w-4" /> Save
            </Button>
          </div>
        }
      />

      {banner && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {banner}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-6">
          {/* Basics */}
          <Card>
            <CardBody className="space-y-4">
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nightly opportunity sync" />
              </Field>
              <Field label="Description" hint="Optional — what this workflow does and why.">
                <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
              </Field>
            </CardBody>
          </Card>

          {/* Trigger */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-slate-900">Trigger</h3>
              <p className="mt-0.5 text-xs text-slate-400">What starts this workflow.</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <TriggerChip icon={Play} label="Manual" active={trigger.type === "manual"} onClick={() => setTrigger({ type: "manual" })} />
                <TriggerChip icon={Clock} label="Schedule" active={trigger.type === "schedule"} onClick={() => setTrigger({ type: "schedule", cron: trigger.cron || "0 1 * * *" })} />
                <TriggerChip icon={Webhook} label="On event" active={trigger.type === "webhook"} onClick={() => setTrigger({ type: "webhook", connection: trigger.connection || "", event: trigger.event || "" })} />
              </div>

              {trigger.type === "schedule" && (
                <div className="mt-4">
                  <Field label="Cron schedule" hint="5 fields: minute hour day month weekday. e.g. 0 1 * * * = every day at 01:00.">
                    <Input value={trigger.cron ?? ""} onChange={(e) => setTrigger({ type: "schedule", cron: e.target.value })} placeholder="0 1 * * *" />
                  </Field>
                  {errors["trigger.cron"] && <p className="mt-1 text-xs text-rose-600">{errors["trigger.cron"]}</p>}
                </div>
              )}
              {trigger.type === "webhook" && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <Field label="Connection">
                    <Select value={trigger.connection ?? ""} onChange={(e) => setTrigger({ ...trigger, type: "webhook", connection: e.target.value })}>
                      <option value="">Choose…</option>
                      {conns.map((c) => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
                    </Select>
                    {errors["trigger.connection"] && <p className="mt-1 text-xs text-rose-600">{errors["trigger.connection"]}</p>}
                  </Field>
                  <Field label="Event">
                    <Input value={trigger.event ?? ""} onChange={(e) => setTrigger({ ...trigger, type: "webhook", event: e.target.value })} placeholder="e.g. ticket.created" />
                    {errors["trigger.event"] && <p className="mt-1 text-xs text-rose-600">{errors["trigger.event"]}</p>}
                  </Field>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Steps */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Steps</h3>
              {errors["steps"] && <span className="text-xs text-rose-600">{errors["steps"]}</span>}
            </div>

            {steps.map((step, i) => (
              <StepCard
                key={step.key}
                index={i}
                step={step}
                conns={conns}
                conn={step.connection ? connBySlug[step.connection] : undefined}
                priorKeys={priorKeys(i)}
                errors={errors}
                first={i === 0}
                last={i === steps.length - 1}
                onPatch={(p) => patchStep(i, p)}
                onPickConnection={(s) => pickConnection(i, s)}
                onPickCapability={(c) => pickCapability(i, c)}
                onSetParam={(k, v) => patchStep(i, { params: { ...step.params, [k]: v } })}
                onRemoveParam={(k) => {
                  const p = { ...step.params };
                  delete p[k];
                  patchStep(i, { params: p });
                }}
                onMove={(d) => move(i, d)}
                onRemove={() => setSteps((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : prev))}
              />
            ))}

            <button
              onClick={() => setSteps((prev) => [...prev, blankStep()])}
              className="focus-ring flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white/60 py-3 text-sm font-medium text-slate-500 transition hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700"
            >
              <Plus className="h-4 w-4" /> Add step
            </button>
          </div>
        </div>

        {/* Side: run result + help */}
        <aside className="space-y-4">
          {run ? (
            <RunPanel run={run} />
          ) : (
            <Card className="bg-slate-900 text-white">
              <CardBody>
                <Zap className="h-5 w-5 text-accent-300" />
                <h3 className="mt-3 text-sm font-semibold">How steps connect</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Reference an earlier step's output in any field with{" "}
                  <code className="rounded bg-white/10 px-1.5 py-0.5 text-xs">{"{{ step1.records }}"}</code>.
                  Every connector speaks the same five verbs — query, create, update, delete, subscribe —
                  so steps look the same whatever system they hit.
                </p>
              </CardBody>
            </Card>
          )}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
            <p className="font-semibold text-slate-700">Runs in the sandbox</p>
            <p className="mt-1.5">Each step executes its connector in an isolated gVisor sandbox using the connection's saved credentials.</p>
          </div>
        </aside>
      </div>
    </>
  );
}

function TriggerChip({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof Play;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "focus-ring flex flex-col items-center gap-1.5 rounded-xl border py-3 text-xs font-semibold transition",
        active
          ? "border-brand-400 bg-brand-50 text-brand-700 ring-1 ring-brand-200"
          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700",
      )}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function StepCard({
  index,
  step,
  conns,
  conn,
  priorKeys,
  errors,
  first,
  last,
  onPatch,
  onPickConnection,
  onPickCapability,
  onSetParam,
  onRemoveParam,
  onMove,
  onRemove,
}: {
  index: number;
  step: WorkflowStep;
  conns: BuilderConnection[];
  conn?: BuilderConnection;
  priorKeys: string[];
  errors: Record<string, string>;
  first: boolean;
  last: boolean;
  onPatch: (p: Partial<WorkflowStep>) => void;
  onPickConnection: (slug: string) => void;
  onPickCapability: (cap: string) => void;
  onSetParam: (k: string, v: string) => void;
  onRemoveParam: (k: string) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const [newParam, setNewParam] = useState("");
  const err = (field: string) => errors[`steps[${index}].${field}`];

  return (
    <Card className={cn(err("connection") || err("capability") ? "ring-1 ring-rose-200" : "")}>
      <CardBody className="space-y-3.5">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white">
            {index + 1}
          </span>
          <Input
            className="flex-1"
            value={step.name}
            onChange={(e) => onPatch({ name: e.target.value })}
            placeholder={`Step ${index + 1} name (optional)`}
          />
          <div className="flex items-center gap-0.5">
            <button disabled={first} onClick={() => onMove(-1)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Move up">
              <ChevronUp className="h-4 w-4" />
            </button>
            <button disabled={last} onClick={() => onMove(1)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Move down">
              <ChevronDown className="h-4 w-4" />
            </button>
            <button onClick={onRemove} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-50" aria-label="Remove step">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Connection</label>
            <div className="relative">
              {conn && (
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
                  <SystemLogo iconSlug={conn.icon_hint} systemType={conn.system_type} name={conn.display_name} size={18} />
                </span>
              )}
              <Select
                className={cn(conn && "pl-9")}
                value={step.connection ?? ""}
                onChange={(e) => onPickConnection(e.target.value)}
              >
                <option value="">Choose a connection…</option>
                {conns.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.display_name}</option>
                ))}
              </Select>
            </div>
            {err("connection") && <p className="mt-1 text-xs text-rose-600">{err("connection")}</p>}
            {conn && !conn.has_code && (
              <p className="mt-1 text-xs text-amber-600">This connection has no runnable code yet.</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Operation</label>
            <Select
              value={step.capability ?? ""}
              onChange={(e) => onPickCapability(e.target.value)}
              disabled={!conn}
            >
              <option value="">{conn ? "Choose an operation…" : "Pick a connection first"}</option>
              {(conn?.capabilities ?? []).map((cap) => (
                <option key={cap} value={cap}>{cap}</option>
              ))}
            </Select>
            {err("capability") && <p className="mt-1 text-xs text-rose-600">{err("capability")}</p>}
          </div>
        </div>

        {step.capability && (
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Parameters</p>
            <div className="space-y-2">
              {Object.keys(step.params).length === 0 && (
                <p className="text-xs text-slate-400">No parameters. Add one below if the operation needs it.</p>
              )}
              {Object.entries(step.params).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="w-28 shrink-0 truncate rounded-lg bg-white px-2.5 py-2 text-xs font-medium text-slate-600 ring-1 ring-slate-200">{k}</span>
                  <Input
                    className="flex-1 text-sm"
                    value={typeof v === "string" ? v : JSON.stringify(v)}
                    onChange={(e) => onSetParam(k, e.target.value)}
                    placeholder={priorKeys.length ? `value or {{ ${priorKeys[priorKeys.length - 1]}.records }}` : "value"}
                  />
                  <button onClick={() => onRemoveParam(k)} className="rounded-lg p-1.5 text-slate-300 hover:bg-slate-100 hover:text-rose-500" aria-label={`Remove ${k}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Input
                className="h-8 flex-1 text-xs"
                value={newParam}
                onChange={(e) => setNewParam(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newParam.trim()) {
                    onSetParam(newParam.trim(), "");
                    setNewParam("");
                  }
                }}
                placeholder="add a parameter name, press Enter"
              />
            </div>
            {priorKeys.length > 0 && (
              <p className="mt-2 text-[11px] text-slate-400">
                Reference earlier steps: {priorKeys.map((k) => (
                  <code key={k} className="mx-0.5 rounded bg-white px-1 py-0.5 ring-1 ring-slate-200">{`{{ ${k} }}`}</code>
                ))}
              </p>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function RunPanel({ run }: { run: WorkflowRun }) {
  const ok = run.status === "succeeded";
  return (
    <Card className={cn("ring-1", ok ? "ring-emerald-200" : "ring-rose-200")}>
      <CardBody>
        <div className={cn("flex items-center gap-2 text-sm font-semibold", ok ? "text-emerald-700" : "text-rose-700")}>
          {ok ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          Run {run.status}
        </div>
        <ol className="mt-3 space-y-2">
          {(run.steps ?? []).map((s) => (
            <li key={s.key} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-slate-700">{s.name || s.key}</span>
                <StepStatus status={s.status} />
              </div>
              <p className="mt-0.5 truncate text-xs text-slate-400">{s.capability}</p>
              {s.error && <p className="mt-1 text-xs text-rose-600">{s.error}</p>}
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  );
}

function StepStatus({ status }: { status: string }) {
  const map: Record<string, string> = {
    ok: "bg-emerald-100 text-emerald-700",
    error: "bg-rose-100 text-rose-700",
    skipped: "bg-slate-100 text-slate-500",
    running: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", map[status] ?? map.skipped)}>
      {status}
    </span>
  );
}
